import type { TechnologySelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { TechnologyTrlDetails } from '@components/pages/technology/components/technology-trl-details.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Score } from '@components/shared/score.component';
import { cn } from '@lib/utils';
import { useGetTechnologyTrlAssessment } from '@queries/technology.query';
import { formatProper } from '@utils/text.util';
import { ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { FunctionComponent } from 'react';
import { useState } from 'react';

export type TechnologyGridItem = {
  categoryName?: string;
  id: TechnologySelectDto['id'];
  name: TechnologySelectDto['name'];
  trlScore?: number;
  type?: TechnologySelectDto['type'];
};

type TechnologyContainerProps = {
  className?: string;
  onNavigateToDetails: (id: string) => void;
  technology: TechnologyGridItem;
};

export const TechnologyContainer: FunctionComponent<TechnologyContainerProps> = (props) => {
  const { className, onNavigateToDetails, technology } = props;
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const shouldFetchFeaturedAssessment = isExpanded || technology.trlScore === undefined;
  const { data: featuredAssessment } = useGetTechnologyTrlAssessment(technology.id, {
    enabled: shouldFetchFeaturedAssessment,
  });
  const label = technology.type ? formatProper(technology.type) : (technology.categoryName ?? 'Technology');
  const trlScore = featuredAssessment?.score ?? technology.trlScore;

  return (
    <div
      className={cn('bg-white border-gray-200 border min-w-[200px] rounded-xl p-4', className)}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <div>
            <p
              className="text-lg font-medium text-gray-600 flex items-center cursor-pointer hover:underline"
              onClick={() => onNavigateToDetails(technology.id)}
            >
              {technology.name}
            </p>
            <BlueLabel text={label} />
          </div>
        </div>
        <IconButton
          icon={<ArrowRight />}
          isVisible={isHovering}
          onClick={() => onNavigateToDetails(technology.id)}
          variant="secondary"
          ariaLabel="Technology Details"
        />
      </div>
      <div className="mt-4 bg-gray-100 px-2 py-1 rounded-xl transition-all">
        <div className="flex items-center justify-between px-1">
          <div className="py-2">
            <Score
              score={trlScore}
              size="medium"
              title="TRL"
              subtitle="Score"
              type="TRL"
            />
          </div>

          {(featuredAssessment || technology.trlScore !== undefined) && (
            <div
              className="cursor-pointer"
              onClick={() => setIsExpanded((prev) => !prev)}
            >
              {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          )}
        </div>

        {isExpanded && featuredAssessment && (
          <section className="pb-2">
            <div className="w-full h-px mt-2 mb-4 bg-gray-200" />
            <TechnologyTrlDetails featuredAssessment={featuredAssessment} />
          </section>
        )}
      </div>
    </div>
  );
};
